import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "sushiweb/Modal";
import OtpInput from "react-otp-input";

import useOnboardingStore from "./useOnboardingStore";
import { STATUS_BEGIN } from "utils/constants/others";
import {
  IDENTIFIER_TYPE,
  OTP_LENGTH,
  validateIdentifier,
} from "utils/constants/onboarding";
import styles from "./Onboarding.module.scss";

const VIEW = { IDENTIFIER: "IDENTIFIER", OTP: "OTP" };
const RESEND_SECONDS = 30;

const formatTimer = (sec) =>
  `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

const LockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1" />
  </svg>
);
const MessageIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="9" y1="10" x2="9" y2="10" />
    <line x1="12" y1="10" x2="12" y2="10" />
    <line x1="15" y1="10" x2="15" y2="10" />
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

function OnboardingAuthModal({ visible, onClose }) {
  const navigate = useNavigate();

  const requestSignupOtp = useOnboardingStore((s) => s.requestSignupOtp);
  const verifyOtpAndOnboard = useOnboardingStore((s) => s.verifyOtpAndOnboard);
  const signupOtpStatus = useOnboardingStore((s) => s.signupOtpStatus);
  const onboardStatus = useOnboardingStore((s) => s.onboardStatus);

  const [view, setView] = useState(VIEW.IDENTIFIER);
  const [identifierType, setIdentifierType] = useState(IDENTIFIER_TYPE.PHONE);
  const [value, setValue] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(RESEND_SECONDS);
  const timerRef = useRef(null);

  const isSending = signupOtpStatus === STATUS_BEGIN;
  const isVerifying = onboardStatus === STATUS_BEGIN;
  const isPhone = identifierType === IDENTIFIER_TYPE.PHONE;

  useEffect(() => {
    if (visible) {
      setView(VIEW.IDENTIFIER);
      setValue("");
      setOtp("");
      setError("");
    }
  }, [visible]);

  useEffect(() => {
    if (view !== VIEW.OTP) return undefined;
    setSeconds(RESEND_SECONDS);
    timerRef.current = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [view]);

  const switchType = (type) => {
    setIdentifierType(type);
    setValue("");
    setError("");
  };

  const onIdentifierChange = (e) => {
    let next = e.target.value;
    if (isPhone) next = next.replace(/\D/g, "").slice(0, 10);
    setValue(next);
    if (error) setError("");
  };

  const submitIdentifier = async (e) => {
    e.preventDefault();
    const validationError = validateIdentifier(identifierType, value);
    if (validationError) {
      setError(validationError);
      return;
    }
    const res = await requestSignupOtp(identifierType, value.trim());
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setOtp("");
    setError("");
    setView(VIEW.OTP);
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    const res = await verifyOtpAndOnboard(identifierType, value.trim(), otp);
    if (!res.ok) {
      setError(res.message);
      setOtp("");
      return;
    }
    onClose?.();
    navigate("/onboarding/form");
  };

  const resend = async () => {
    if (seconds > 0) return;
    setError("");
    setOtp("");
    const res = await requestSignupOtp(identifierType, value.trim(), true);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setSeconds(RESEND_SECONDS);
  };

  const maskedTarget = isPhone
    ? `+91 ${value}`
    : value;

  return (
    <Modal visible={visible} isCentered onClose={onClose} showCloseIcon={false}>
      <div className={styles.modalBody}>
        {view === VIEW.IDENTIFIER ? (
          <form onSubmit={submitIdentifier} noValidate>
            <div className={styles.iconCircle}>
              <LockIcon />
            </div>
            <h2 className={styles.title}>Register</h2>
            <p className={styles.subtitle}>
              Enter your phone number or email to get started
            </p>

            <div className={styles.tabs}>
              <button
                type="button"
                className={`${styles.tab} ${isPhone ? styles.tabActive : ""}`}
                onClick={() => switchType(IDENTIFIER_TYPE.PHONE)}
              >
                Phone
              </button>
              <button
                type="button"
                className={`${styles.tab} ${!isPhone ? styles.tabActive : ""}`}
                onClick={() => switchType(IDENTIFIER_TYPE.EMAIL)}
              >
                Email
              </button>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {isPhone ? "Phone Number" : "Email Address"}
              </label>
              <div className={styles.inputWrap}>
                <span className={styles.inputPrefix}>
                  {isPhone ? <><PhoneIcon /> +91</> : <MailIcon />}
                </span>
                <input
                  className={styles.input}
                  type={isPhone ? "tel" : "email"}
                  inputMode={isPhone ? "numeric" : "email"}
                  placeholder={isPhone ? "10-digit mobile number" : "you@company.com"}
                  value={value}
                  onChange={onIdentifierChange}
                  maxLength={isPhone ? 10 : undefined}
                  autoFocus
                />
              </div>
              {error && <div className={styles.errorText}>{error}</div>}
            </div>

            <button className={styles.primaryBtn} type="submit" disabled={isSending || !value}>
              {isSending ? "Sending OTP…" : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitOtp} noValidate>
            <div className={styles.iconCircle}>
              <MessageIcon />
            </div>
            <h2 className={styles.title}>Verify OTP</h2>
            <p className={styles.subtitle}>
              Code sent to <strong>{maskedTarget}</strong>
            </p>

            <div className={styles.otpRow}>
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v.replace(/\D/g, ""));
                  if (error) setError("");
                }}
                numInputs={OTP_LENGTH}
                shouldAutoFocus
                inputType="tel"
                renderInput={(props) => (
                  <input {...props} className={styles.otpBox} />
                )}
              />
            </div>

            <div className={styles.otpFooter}>
              <span className={styles.otpTimer}>{formatTimer(seconds)}</span>
              Didn't receive?{" "}
              <button
                type="button"
                className={styles.resendLink}
                onClick={resend}
                disabled={seconds > 0}
              >
                Resend
              </button>
            </div>

            {error && (
              <div
                className={styles.errorText}
                style={{ textAlign: "center", marginTop: 8 }}
              >
                {error}
              </div>
            )}

            <button
              className={styles.primaryBtn}
              type="submit"
              disabled={isVerifying || otp.length !== OTP_LENGTH}
            >
              {isVerifying ? "Verifying…" : "Verify & Continue"}
            </button>
          </form>
        )}
      </div>
    </Modal>
  );
}

export default OnboardingAuthModal;
